import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';
import {
  Brain, Loader2, RefreshCw, Search, ChevronLeft, ChevronRight,
  Filter, Calendar, User, Hash, Trophy, Zap, Crown, Sparkles, Bot,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';

const ENGINE_CONFIG = {
  'Aries': { color: 'bg-amber-500/15 text-amber-400 border-amber-500/30', icon: Sparkles },
  '7IA': { color: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30', icon: Zap },
  'Jugada del Dia': { color: 'bg-purple-500/15 text-purple-400 border-purple-500/30', icon: Trophy },
  'Super IA Master': { color: 'bg-rose-500/15 text-rose-400 border-rose-500/30', icon: Brain },
  'Sócrates Master': { color: 'bg-rose-500/15 text-rose-400 border-rose-500/30', icon: Brain },
  'Sócrates': { color: 'bg-purple-500/15 text-purple-400 border-purple-500/30', icon: Brain },
  'Bot Diario': { color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', icon: Bot },
  'Prometeo': { color: 'bg-orange-500/15 text-orange-400 border-orange-500/30', icon: Zap },
  'Poderoso': { color: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30', icon: Crown },
  'Pale': { color: 'bg-blue-500/15 text-blue-400 border-blue-500/30', icon: Hash },
  'Tripleta': { color: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30', icon: Hash },
  'Auto': { color: 'bg-slate-500/15 text-slate-400 border-slate-500/30', icon: Brain },
};

const getEngineStyle = (engine) => {
  for (const [key, val] of Object.entries(ENGINE_CONFIG)) {
    if (engine?.toLowerCase().includes(key.toLowerCase())) return val;
  }
  return ENGINE_CONFIG['Auto'];
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-DO', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return dateStr?.slice(0, 16) || '-';
  }
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <Card className="bg-gray-900/80 border-gray-800" data-testid={`stat-${label}`}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
        <Icon size={18} />
      </div>
      <div>
        <p className="text-2xl font-black text-white">{value}</p>
        <p className="text-xs text-gray-500">{label}</p>
      </div>
    </CardContent>
  </Card>
);

export default function AdminPredictions() {
  const { token } = useAuth();
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [engineFilter, setEngineFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [engineCounts, setEngineCounts] = useState({});

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchPredictions = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/predictions`, {
        headers: headers(),
        params: { engine: engineFilter, page: p, limit: 50 },
      });
      setPredictions(res.data.predictions || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setEngineCounts(res.data.engine_counts || {});
      setPage(p);
    } catch (e) {
      toast.error('Error cargando predicciones');
    }
    setLoading(false);
  }, [token, engineFilter, headers]);

  useEffect(() => { if (token) fetchPredictions(1); }, [token, engineFilter, fetchPredictions]);

  const filtered = search
    ? predictions.filter(p =>
        p.lottery_name?.toLowerCase().includes(search.toLowerCase()) ||
        p.user_email?.toLowerCase().includes(search.toLowerCase()) ||
        p.user_name?.toLowerCase().includes(search.toLowerCase()) ||
        p.engine?.toLowerCase().includes(search.toLowerCase())
      )
    : predictions;

  const totalAll = Object.values(engineCounts).reduce((a, b) => a + b, 0);

  const engines = [
    { key: 'all', label: 'Todos' },
    { key: 'prometeo', label: 'Prometeo' },
    { key: 'aries', label: 'Aries' },
    { key: '7ia', label: '7IA' },
    { key: 'jugada', label: 'Jugada' },
    { key: 'power-number', label: 'Poderoso' },
    { key: 'super-ia', label: 'Sócrates' },
    { key: 'bot-diario', label: 'Bot Diario' },
    { key: 'pale-tripleta', label: 'Pale/Trip' },
  ];

  return (
    <AdminLayout title="Predicciones" subtitle="Detalle de predicciones generadas por clientes">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="prediction-stats">
        <StatCard label="Total" value={totalAll} icon={Brain} color="bg-purple-500/20 text-purple-400" />
        {Object.entries(engineCounts).slice(0, 5).map(([eng, count]) => {
          const style = getEngineStyle(eng);
          const Icon = style.icon;
          return (
            <StatCard key={eng} label={eng} value={count} icon={Icon} color={style.color} />
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4" data-testid="prediction-filters">
        <div className="flex gap-1 flex-wrap">
          {engines.map(e => (
            <Button
              key={e.key}
              variant={engineFilter === e.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setEngineFilter(e.key)}
              className={engineFilter === e.key
                ? 'bg-amber-600 hover:bg-amber-500 text-white'
                : 'border-gray-700 text-gray-400 hover:text-white'}
              data-testid={`filter-${e.key}`}
            >
              {e.label}
            </Button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input
            placeholder="Buscar usuario, loteria..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-gray-900 border-gray-700 text-white pl-9 h-9"
            data-testid="search-predictions"
          />
        </div>
        <Button
          variant="outline" size="sm" onClick={() => fetchPredictions(page)}
          disabled={loading}
          className="border-gray-700 text-gray-400 hover:text-white"
          data-testid="refresh-predictions"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-amber-400" size={28} />
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-gray-800 overflow-hidden" data-testid="predictions-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-900/80 border-b border-gray-800">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400">Motor</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400">Usuario</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400">Loteria</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400">Numeros</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400">Confianza</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p, i) => {
                    const style = getEngineStyle(p.engine);
                    const Icon = style.icon;
                    const confColor = p.confidence >= 80 ? 'text-emerald-400'
                      : p.confidence >= 60 ? 'text-amber-400' : 'text-gray-400';
                    return (
                      <tr
                        key={`${p.engine}-${p.created_at}-${i}`}
                        className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors"
                        data-testid={`prediction-row-${i}`}
                      >
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={`text-[10px] ${style.color} gap-1`}>
                            <Icon size={10} /> {p.engine}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-white text-xs font-medium">{p.user_name || '-'}</p>
                            <p className="text-gray-500 text-[10px]">{p.user_email || '-'}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-white text-xs">{p.lottery_name}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            {(p.numbers || []).slice(0, 3).map((n, j) => (
                              <span
                                key={`${p.engine}-${p.created_at}-${n}-${j}`}
                                className="w-7 h-7 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center text-[10px] font-bold text-white"
                              >
                                {String(n).padStart(2, '0')}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-bold ${confColor}`}>
                            {typeof p.confidence === 'number' ? `${p.confidence.toFixed(1)}%` : '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-gray-400 text-[10px]">{formatDate(p.created_at)}</span>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                        No se encontraron predicciones
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between mt-4" data-testid="pagination">
              <span className="text-gray-500 text-xs">
                Mostrando {(page - 1) * 50 + 1}-{Math.min(page * 50, total)} de {total}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline" size="sm" disabled={page <= 1}
                  onClick={() => fetchPredictions(page - 1)}
                  className="border-gray-700 text-gray-400"
                >
                  <ChevronLeft size={14} />
                </Button>
                <span className="text-white text-sm px-3 py-1">
                  {page} / {pages}
                </span>
                <Button
                  variant="outline" size="sm" disabled={page >= pages}
                  onClick={() => fetchPredictions(page + 1)}
                  className="border-gray-700 text-gray-400"
                >
                  <ChevronRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}
